<template>
  <div class="container-fluid">
    <PageHeader :page="page">
      <div class="d-flex justify-content-end h-100 align-items-end">
        <ActionButton
          text="Regresar"
          class="m-2"
          i="fas fa-arrow-circle-left"
          variant="outline-success"
          @click="$router.go(-1)"
        />
        <ActionButton
          v-if="hasViewOtherSchoolEnrollmentPermission"
          text="Consulta Matricula Otra Escuela"
          class="m-2"
          variant="success"
          @click="onGoToOtherSchoolEnrollment"
        />
      </div>
    </PageHeader>
    <FilterManager
      ref="filterManager"
      v-model="filters"
      :filters="filtersHeader"
      :open-advanced="isAdvanced"
      @academicYear="onAcademicYearChanged"
      @center="onCenterChanged"
      @municipality="onMunicipalityChanged"
      @alternativeCategory="onAlternativeCategoryChanged"
      @alternative="onAlternativeChanged"
      @search="onSearch"
    />

    <CustomSeparator
      text="Resultado de Manejo de Alternativa de Ubicación"
      class="my-4 custom-separator-line"
    />

    <CustomResultNotFound
      v-if="showNotResults"
      class="mt-4 row pt-3"
      text="No hay información disponible."
    />

    <template v-if="isAdvanced">
      <SearchResultRowAdvanced
        v-for="item in collections.searchResult"
        :key="`row-item-${item.id}`"
        ref="resultRow"
        :value="item"
      />
    </template>
    <template v-else>
      <SearchResultRowSimple
        v-for="item in collections.searchResult"
        :key="`row-item-${item.academicYear.id}-${item.school.id}`"
        ref="resultRow"
        :value="item"
      />
    </template>

    <Pagination
      v-if="pagination.totalItemCount > pagination.perPage"
      :page-size="pagination.perPage"
      :data="pagination"
      @pagination-go-page="onPageChanged"
    />
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import SearchResultRowSimple from './components/SearchResultRowSimple';
import SearchResultRowAdvanced from './components/SearchResultRowAdvanced';
import academicYearService from '@/services/academicyear';
import locationService from '@/services/api/location.api.js';
import storeMixin from '@/mixin/storeGenerator';
import SchoolTypes from '@/utils/constants/student/schoolTypes';
import * as locationAlternativeApi from '@/views/pei/location-alternatives/_api';
import {
  getLocationSchools,
  getSchoolAlternativeLocations,
} from '@/modules/alternative-location-management/api';
import alert from '@/mixin/alert.mixin';

const updatedQuotaOptions = [
  { id: 2, name: 'Si' },
  { id: 1, name: 'No' },
];

export default {
  name: 'LocationAlternativeManagement',
  mixins: [storeMixin, alert],
  components: {
    PageHeader,
    ActionButton,
    Pagination,
    FilterManager,
    CustomResultNotFound,
    CustomSeparator,
    SearchResultRowSimple,
    SearchResultRowAdvanced,
  },
  data() {
    return {
      page: {
        icon: 'fas fa-user-edit',
        title: 'Manejo de Alternativa de Ubicación',
        routes: [
          'Inicio',
          'Recursos Humanos',
          'Manejo de Alternativa de Ubicación',
        ],
      },
      collections: {
        academicYears: [],
        centers: [],
        municipalities: [],
        schools: [],
        alternativeCategories: [],
        alternatives: [],
        teachers: [],
        grades: [],
        searchResult: [],
      },
      filters: {
        academicYear: null,
        center: null,
        municipality: null,
        school: null,
        alternativeCategory: null,
        alternative: null,
        teacher: null,
        grade: null,
        studentSie: null,
        updatedQuota: null,
      },
      pagination: {
        perPage: 20,
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 0,
        count: 0,
      },
      isAdvanced: false,
    };
  },
  methods: {
    async onAcademicYearChanged(academicYear) {
      this.reset(
        ['center', 'municipality', 'school'],
        ['centers', 'municipalities', 'schools']
      );
      if (academicYear) {
        await this.fetchCenters(academicYear.id);
      }

      if (this.filters.alternativeCategory) {
        this.reset(['teacher'], ['teachers']);
        await this.fetchTeachers(
          academicYear.id,
          this.filters.alternativeCategory.id,
          this.filters.alternative?.id
        );
      }
    },
    async onCenterChanged(center) {
      this.reset(['municipality', 'school'], ['municipalities', 'schools']);
      if (center) {
        await this.fetchMunicipalities(center.id);
      }
    },
    async onMunicipalityChanged(municipality) {
      this.reset(['school'], ['schools']);
      if (municipality) {
        await this.fetchSchools(municipality.id);
      }
    },
    async onAlternativeCategoryChanged(alternativeCategory) {
      this.reset(['alternative'], ['alternatives']);
      if (alternativeCategory) {
        await this.fetchAlternatives(alternativeCategory.id);
      }
    },
    async onAlternativeChanged(alternative) {
      this.reset(['teacher'], ['teachers']);
      await this.fetchTeachers(
        this.filters.academicYear.id,
        this.filters.alternativeCategory.id,
        alternative?.id
      );
    },
    async fetchCenters(academicYearId) {
      const { data } = await locationService.getCenters({
        academicYearId,
      });
      this.collections.centers = data;
      if (this.collections.centers && this.collections.centers.length == 1) {
        this.filters.center = this.collections.centers[0];
        await this.fetchMunicipalities(this.filters.center.id);
      }
    },
    async fetchMunicipalities(centerId) {
      const { data } = await locationService.getMunicipalities({
        centerId,
        academicYearId: this.filters.academicYear.id,
      });
      this.collections.municipalities = data;
      if (
        this.collections.municipalities &&
        this.collections.municipalities.length == 1
      ) {
        this.filters.municipality = this.collections.municipalities[0];
        await this.fetchSchools(this.filters.municipality.id);
      }
    },
    async fetchSchools(municipalityId) {
      const { data } = await locationService.getSchools({
        municipalityId,
        centerId: this.filters.center.id,
        academicYearId: this.filters.academicYear.id,
        schoolTypes: [SchoolTypes.PublicSchool, SchoolTypes.Institutions],
      });
      this.collections.schools = data;
      if (this.collections.schools && this.collections.schools.length == 1) {
        this.filters.school = this.collections.schools[0];
      }
    },
    async fetchAlternativeCategories() {
      const alternativeCategories = await locationAlternativeApi.getCategories();
      this.collections.alternativeCategories = alternativeCategories;
      if (
        this.collections.alternativeCategories &&
        this.collections.alternativeCategories.length == 1
      ) {
        this.filters.alternativeCategory = this.collections.alternativeCategories[0];
        await this.fetchAlternatives(this.filters.alternativeCategory.id);
      }
    },
    async fetchAlternatives(categoryId) {
      const alternatives = await locationAlternativeApi.getNames({
        categoryId,
      });
      this.collections.alternatives = alternatives;
      if (
        this.collections.alternatives &&
        this.collections.alternatives.length == 1
      ) {
        this.filters.alternative = this.collections.alternatives[0];
      }

      this.fetchTeachers(this.filters.academicYear.id, categoryId, null);
    },
    async fetchTeachers(academicYearId, categoryId, subcategoryId) {
      const teachers = await locationAlternativeApi.getTeachers({
        academicYearId,
        categoryId,
        subcategoryId,
      });
      this.collections.teachers = teachers;
      if (this.collections.teachers && this.collections.teachers.length == 1) {
        this.filters.teacher = this.collections.teachers[0];
      }
    },
    async fetchGrades() {
      const grades = await locationAlternativeApi.getGrades();
      this.collections.grades = grades;
    },
    async onSearch(filters, page = 1, isAdvanced = this.isAdvanced) {
      let mappedFilter = {
        academicYearId: filters.academicYear?.id,
        centerId: filters.center?.id,
        municipalityId: filters.municipality?.id,
        schoolId: filters.school?.id,
        categoryId: filters.alternativeCategory?.id,
        alternativeNameId: filters.alternative?.id,
        teacherId: filters.teacher?.id,
        gradeId: filters.grade?.id,
        studentSie: filters.studentSie,
        pageIndex: page,
      };

      const requestData = isAdvanced
        ? getSchoolAlternativeLocations
        : getLocationSchools;

      if (isAdvanced && filters.updatedQuota) {
        mappedFilter = {
          ...mappedFilter,
          updatedQuota: filters.updatedQuota?.id == 2 ? true : false,
        };
      }

      await requestData(mappedFilter).then((res) => {
        this.collections.searchResult = res.data.data;

        if (res.data.totalItemCount == 0 && filters.studentSie) {
          this.ShowToast(
            'Atención',
            'El número de SIE no esta asignado a una alternativa de ubicación.',
            'warning'
          );
        }

        this.pagination = {
          ...this.pagination,
          totalItemCount: res.data.totalItemCount,
          pageCount: res.data.pageCount,
          currentPage: res.data.currentPage,
          count: res.data.count,
        };
        this.isAdvanced = isAdvanced;
      });
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.onSearch(this.filters, currentPage);
    },
    async fetchResources() {
      const {
        data: academicYearsList,
      } = await academicYearService.getAcademicYearsToFutureYear();

      this.collections.academicYears = academicYearsList;
      this.filters.academicYear = academicYearsList.find((e) => e.selected);

      await this.fetchCenters(this.filters.academicYear.id);
      await this.fetchAlternativeCategories();
      await this.fetchGrades();
    },
    reset(fields, collections) {
      fields.forEach((field) => {
        this.filters[field] = null;
      });
      collections.forEach((collection) => {
        this.collections[collection] = [];
      });
    },
    onGoToOtherSchoolEnrollment() {
      this.$router.push({ name: 'OtherSchoolEnrollment' });
    },
  },
  computed: {
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    filtersHeader() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.collections.academicYears,
          value: this.filters.academicYear,
          placeholder: 'Seleccione el año',
          fieldName: 'description',
          fieldKey: 'id',
          clearable: false,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          validations: 'required',
          mapParentValue: this.filters.academicYear == null,
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.collections.centers,
          value: this.filters.center,
          placeholder: 'Centro',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.center == null,
          disabled:
            this.collections.centers && this.collections.centers.length <= 1,
        },
        {
          name: 'municipality',
          component: 'CustomDropDown',
          options: this.collections.municipalities,
          value: this.filters.municipality,
          placeholder: 'Municipio',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.municipality == null,
          disabled:
            this.collections.municipalities &&
            this.collections.municipalities.length <= 1,
        },
        {
          name: 'school',
          component: 'CustomDropDown',
          options: this.collections.schools,
          value: this.filters.school,
          placeholder: 'Escuela',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-sm-6 col-md-6 col-lg-3 ml-0 mr-0',
          classContainer: 'w-100',
          mapParentValue: this.filters.school == null,
          disabled:
            this.collections.schools && this.collections.schools.length <= 1,
        },
        {
          name: 'alternativeCategory',
          label: 'Categoría de Alternativa:',
          component: 'CustomDropDown',
          options: this.collections.alternativeCategories,
          value: this.filters.alternativeCategory,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 col-lg-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.alternativeCategory == null,
          isAdvanced: true,
          disabled:
            this.collections.alternativeCategories &&
            this.collections.alternativeCategories.length <= 1,
        },
        {
          name: 'alternative',
          label: 'Nombre de la Alternativa:',
          component: 'CustomDropDown',
          options: this.collections.alternatives,
          value: this.filters.alternative,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 col-lg-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.alternative == null,
          isAdvanced: true,
          disabled:
            this.collections.alternatives &&
            this.collections.alternatives.length <= 1,
        },
        {
          name: 'teacher',
          label: 'Nombre del Maestro:',
          component: 'CustomDropDown',
          options: this.collections.teachers,
          value: this.filters.teacher,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 col-lg-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          mapParentValue: this.filters.teacher == null,
          isAdvanced: true,
          disabled:
            this.collections.teachers && this.collections.teachers.length <= 1,
        },
        {
          name: 'grade',
          label: 'Grado:',
          component: 'CustomDropDown',
          options: this.collections.grades,
          value: this.filters.grade,
          placeholder: 'Seleccione',
          class: 'col-12 col-sm-6 col-md-4 col-lg-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
          disabled:
            this.collections.grades && this.collections.grades.length <= 1,
        },
        {
          name: 'studentSie',
          label: 'Número de SIE:',
          component: 'CustomInput',
          placeholder: ' Escriba número de SIE',
          class: 'col-12 col-sm-6 col-md-4 col-lg-3 d-flex flex-column',
          type: 'text',
          isAdvanced: true,
        },
        {
          name: 'updatedQuota',
          label: 'Cupo Actualizado:',
          component: 'CustomDropDown',
          placeholder: 'Seleccione',
          options: updatedQuotaOptions,
          value: this.filters.updatedQuota,
          class: 'col-12 col-sm-6 col-md-4 col-lg-3 d-flex flex-column',
          fieldName: 'name',
          fieldKey: 'id',
          isAdvanced: true,
        },
      ];
    },
    showNotResults() {
      return this.collections.searchResult.length == 0;
    },
    hasViewOtherSchoolEnrollmentPermission() {
      return this.$store.getters.HasPermission(
        'menu.human.resources.locationalternativemanagement.otherschoolstudentenrollment.view'
      );
    },
  },
  destroyed() {
    this.persistData();
  },
  beforeRouteEnter(_, from, next) {
    const persistRoutes = ['Management'];

    next(async (vm) => {
      if (persistRoutes.some((x) => x == from.name)) {
        vm.recoveryData();
      } else {
        vm.shouldRecovery = false;
        vm.isAdvanced = false;

        await vm.fetchResources();
      }
    });
  },
  mounted() {
    this.fetchResources();
    this.$watch('$refs.filterManager.advanced', (value) => {
      if (this.collections.searchResult.length)
        this.onSearch(this.filters, null, value);
      else this.isAdvanced = value;
    });

    this.onSearch(this.filters, null, this.isAdvanced);
  },
};
</script>

<style lang="scss" scoped>
::v-deep .vs__dropdown-toggle {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
